import { reverse, sortBy } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useFilterBarParams } from 'src/common/components/filter-bar/use-params';
import {
  TableSortableColumnHeader,
  TableSortableColumnHeaderOrderType,
  TableSortableColumnHeaderSortType,
} from 'src/common/components/table/sortable-column-header';
import { generateColumn } from 'src/common/components/table/LegacyTable';
import { Node } from 'src/common/interfaces/node.interface';
import { DataTable } from 'src/common/components/table/data-table';
import classNames from 'classnames';
import { IconButton } from 'src/common/icon-button';
import ChevronUpStrongIcon from 'src/monet/icons/ChevronUpStrongIcon';
import { Journey } from 'src/common/interfaces/journey.interface';
import { useJourneyInsightsByStep } from 'src/dashboard/insights/journeys/use-journey-insights-by-step.hook';
import { ContentDict } from 'src/editor/insights/constants';
import { formatDuration } from 'src/utils/date';
import { DashboardInsightsJourneysJourneyDocumentInsights } from './document-insights';
import { TimePeriodParam } from '../../constants';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { useJourneyDocumentInsights } from '../use-journey-document-insights.hook';
import { formatNumber } from 'src/utils/text';
import { LibraryHoverableCardSmall } from 'src/common/components/library/hoverable-content-card-small';
import { useJourneyInsights } from '../use-journey-insights.hook';

type Props = {
  journey: Journey;
  contentDict: ContentDict;
  contentList: Node[];
  timePeriodParam?: TimePeriodParam;
  setAllNativeFormsFetcher: (fetcher: () => Promise<{ forms: any[] }>) => void;
};

type StepInsightItem = {
  node: Node;
  step_index: number;
  step_name: string;
  view_count: number;
  average_time: number;
  ctr_percentage: number;
  expandable: boolean;
};

type EditorInsightsStepsListHeaderTypes = 'number' | 'step_name' | 'view_count' | 'average_time' | 'ctr';

type EditorInsightsStepsListHeaderSortType = TableSortableColumnHeaderSortType<EditorInsightsStepsListHeaderTypes>;

export const DashboardInsightsJourneysJourneyStepsTable: FC<Props> = ({
  contentDict,
  contentList,
  journey,
  timePeriodParam,
  setAllNativeFormsFetcher,
}) => {
  const {
    filterBarParams: { search_term, sort_by, page, sort_order, filters },
    updateSort,
  } = useFilterBarParams({ dontDebounceSearch: true, sort_by: 'number', sort_order: 'asc' });

  const onTableHeaderClick = useCallback(
    (sortStack: EditorInsightsStepsListHeaderSortType) => {
      updateSort(sortStack.sortName, sortStack.orderBy);
    },
    [updateSort]
  );

  const insightsFilters = useMemo(() => ({ session_uuid: undefined, time_period: timePeriodParam }), [timePeriodParam]);
  const { fetching, stepInsights } = useJourneyInsightsByStep({
    journeyUuId: journey?.uuid,
    filters: insightsFilters,
  });

  const documentInsightsParams = useMemo(
    () => ({
      journeyUuId: journey.uuid,
      nodeIds: contentList.map((node) => node.id),
      filters: { session_uuid: undefined, time_period: timePeriodParam },
    }),
    [journey, contentList, timePeriodParam]
  );
  const { fetching: fetchingDocumentInsights, documentInsights } = useJourneyDocumentInsights(documentInsightsParams);

  const { fetchJourneyNativeForms } = useJourneyInsights({
    journeyUuid: journey.uuid,
    page,
    preventImmediateFetching: true,
    filters: insightsFilters,
  });

  useEffect(() => {
    setAllNativeFormsFetcher(fetchJourneyNativeForms);
  }, [fetchJourneyNativeForms]);

  const columns = useMemo(
    () => [
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'number'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='number'
            onClick={onTableHeaderClick}
            title='No'
            tooltip='Step order within Journey'
          />
        ),
        accessor: 'number',
        width: '6%',
        Cell: ({ row, index }: { row: any; index: number }) => {
          const { step_index } = row.original as StepInsightItem;
          return <div {...(row.original.expandable && row.getToggleRowExpandedProps())}>{step_index + 1}</div>;
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'step_name'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='step_name'
            onClick={onTableHeaderClick}
            title='Step'
            tooltip='Step name within Journey'
          />
        ),
        accessor: 'step_name',
        width: '39%',
        Cell: ({ row, index }: { row: any; index: number }) => {
          const node = row.original.node as Node;
          return (
            <div
              className='flex gap-4 items-center'
              {...(row.original.expandable && row.getToggleRowExpandedProps())}
              style={{ wordBreak: 'break-word' }}
            >
              <LibraryHoverableCardSmall content={node} hidePreview={node?.type?.name === 'neue'} />
            </div>
          );
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'view_count'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='view_count'
            onClick={onTableHeaderClick}
            title='Views'
            tooltip='Total step views within Journey'
          />
        ),
        accessor: 'view_count',
        width: '8%',
        Cell: ({ row, index }: { row: any; index: number }) => (
          <div
            className='text-bedrock-p text-bedrock-black'
            {...(row.original.expandable && row.getToggleRowExpandedProps())}
          >
            {formatNumber(row.original.view_count)}
          </div>
        ),
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'average_time'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='average_time'
            onClick={onTableHeaderClick}
            title='Average time'
            tooltip='Average time spent on this step'
          />
        ),
        accessor: 'average_time',
        width: '10%',
        Cell: ({ row, index }: { row: any; index: number }) => {
          const { average_time } = row.original as StepInsightItem;
          return (
            <div
              className='text-bedrock-p text-bedrock-black'
              {...(row.original.expandable && row.getToggleRowExpandedProps())}
            >
              {average_time > 0 ? formatDuration(average_time) : '-'}
            </div>
          );
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'ctr'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='ctr'
            onClick={onTableHeaderClick}
            title='Click-through rate'
            tooltip='How many users view this step'
          />
        ),
        accessor: 'ctr',
        width: '11%',
        Cell: ({ row, index }: { row: any; index: number }) => {
          const { ctr_percentage } = row.original as StepInsightItem;
          return (
            <div
              className='text-bedrock-p text-bedrock-black'
              {...(row.original.expandable && row.getToggleRowExpandedProps())}
            >
              {ctr_percentage ? `${ctr_percentage}%` : '-'}
            </div>
          );
        },
      }),
      generateColumn({
        Header: '',
        accessor: 'actions',
        width: '26%',
        allowShrinking: true,
        Cell: ({ row }: { row: any }) => {
          if (!row.original.expandable) {
            return null;
          }
          return (
            <div className='mr-2 flex items-center' onClick={(e) => e.stopPropagation()}>
              <div className='flex justify-end w-full' {...row.getToggleRowExpandedProps()}>
                <IconButton>
                  <ChevronUpStrongIcon
                    className={classNames('transition-transform', {
                      '-rotate-180': !row.isExpanded,
                    })}
                  />
                </IconButton>
              </div>
            </div>
          );
        },
      }),
    ],
    [sort_by, sort_order, onTableHeaderClick]
  );

  const stepInsightData: StepInsightItem[] = useMemo(() => {
    if (fetching) {
      return [];
    }

    let stepData = contentList.map((node, index) => {
      let data = {
        node,
        step_name: node.name,
        step_index: index,
        view_count: 0,
        average_time: 0,
        ctr_percentage: 0,
        expandable: false,
      };
      const contentInsight = stepInsights.find((si) => si.node_id === node.id);
      if (!contentInsight) {
        return data;
      }
      const { num_sessions, total_time, num_journey_sessions } = contentInsight;
      const average_time = num_sessions > 0 ? Math.floor(total_time / num_sessions) : 0;
      const ctr_fraction = num_journey_sessions > 0 ? num_sessions / num_journey_sessions : 0;
      data = {
        ...data,
        view_count: contentInsight.num_sessions,
        average_time,
        ctr_percentage: Math.max(Math.min(Math.round(ctr_fraction * 100), 100), 0),
      };
      return {
        ...data,
        expandable: node.content_type === 'pdf' && data.view_count > 0,
      };
    });

    if (search_term) {
      stepData = stepData.filter((d) => d.step_name.toLowerCase().includes(search_term.toLowerCase()));
    }

    if (sort_by) {
      stepData = sortBy(stepData, [
        (v) =>
          sort_by === 'step_name'
            ? v.step_name
            : sort_by === 'view_count'
            ? v.view_count
            : sort_by === 'average_time'
            ? v.average_time
            : sort_by === 'ctr'
            ? v.ctr_percentage
            : sort_by === 'number'
            ? v.step_index
            : null,
      ]);
      if (sort_order === 'desc') {
        stepData = reverse(stepData);
      }
    }

    return stepData;
  }, [contentDict, contentList, search_term, sort_by, sort_order, fetching, stepInsights]);

  const renderRowSubComponent = useCallback(
    ({ row, index }) => {
      const item = row.original as StepInsightItem;
      if (!item.expandable) {
        return null;
      }
      const nodeDocumentInsights = documentInsights.filter((di) => di.node_id === item.node.id);
      if (!nodeDocumentInsights || nodeDocumentInsights.length === 0) {
        return null;
      }
      return (
        <DashboardInsightsJourneysJourneyDocumentInsights node={item.node} documentInsights={nodeDocumentInsights} />
      );
    },
    [documentInsights]
  );

  if (fetching || fetchingDocumentInsights) {
    return <FixedLoadingSpinner />;
  }

  if (stepInsightData.length === 0) {
    return <div className='text-bedrock-p'>No step views found</div>;
  }
  return (
    <DataTable
      tableClassName='w-full'
      columns={columns}
      data={stepInsightData}
      rowAlignTop={false}
      expandAllRowsByDefault
      disableRowCursorPointer={(row) => !(row.original as StepInsightItem).expandable}
      renderRowSubComponent={renderRowSubComponent}
    />
  );
};
